
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface HeroAction {
  text: string;
  href: string;
  icon?: React.ReactNode;
  variant?: "default" | "outline" | "custom";
  customButton?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
}

interface HeroProps {
  badge?: {
    text: string;
    action: {
      text: string;
      href: string;
    };
  };
  title: string;
  description: string;
  actions: HeroAction[];
  image: string;
}

export function HeroSection({
  badge,
  title,
  description,
  actions,
  image,
}: HeroProps) {
  return (
    <section className="relative bg-background text-foreground py-12 sm:py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8 lg:px-16">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Text Content */}
          <div className="flex-1 text-left">
            {badge && (
              <Badge variant="outline" className="gap-2 mb-6">
                <span className="text-muted-foreground">{badge.text}</span>
                <a href={badge.action.href} className="flex items-center gap-1">
                  {badge.action.text}
                  <ArrowRight className="h-3 w-3" />
                </a>
              </Badge>
            )}

            <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-6">
              {title}
            </h1>

            <p className="text-lg text-muted-foreground mb-8 max-w-[550px]">
              {description}
            </p>

            <div className="flex flex-wrap gap-4">
              {actions.map((action, index) => (
                action.variant === 'custom' ? (
                  <a key={index} href={action.href}>
                    {action.customButton}
                  </a>
                ) : (
                  <Button 
                    key={index} 
                    variant={action.variant} 
                    size="lg"
                    className="text-lg px-12 py-6 h-14"
                    asChild
                  >
                    <a href={action.href} className="flex items-center gap-2" onClick={action.onClick}>
                      {action.text}
                    </a>
                  </Button>
                )
              ))}
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 w-full lg:w-1/2">
            <img
              src={image}
              alt="Hero image"
              className="w-full h-auto rounded-xl shadow-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
